import {
  Button,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from 'reactstrap';
import { memo, useEffect, useState } from 'react';
import { TextField } from '@material-ui/core';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import api from 'services/api';
import { enumStatus, iMaterial } from 'models/MaterialAcessorio';
import { FaDownload } from 'react-icons/fa';
import { downloadFileFromUrl } from 'utils/download';

type ModalFiltroProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  changeStatus: (a: number, b: string) => void;
  orcamentoMaterialId?: number;
  propostaVersaoId?: number;
  orcamentoId?: number;
};

const ModalMaterialAcessorioAprovacao: React.FC<ModalFiltroProps> = ({
  orcamentoMaterialId,
  propostaVersaoId,
  orcamentoId,
  isOpen,
  setIsOpen,
  changeStatus,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [descricao, setDescricao] = useState('');
  const [quantidade, setQuantidade] = useState(0);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [fileToSend, setFileToSend] = useState('');

  const [dados, setDados] = useState<iMaterial>();

  async function buscarDadosMaterial(id: number) {
    const response = await api.get(
      `/material-acessorio/orcamento-material-id/${id}`,
    );

    const convertido = response.data as iMaterial;

    setDados(convertido);

    setDescricao(convertido.descricao);
    setQuantidade(convertido.orcamentoMaterial.quantidade);
    setIsBlocked(!['A'].includes(convertido.status));
  }

  useEffect(() => {
    if (orcamentoMaterialId) buscarDadosMaterial(orcamentoMaterialId);
  }, [orcamentoMaterialId]);

  async function uploadFile(referenciaId: number) {
    const toastIdAnexo = toast.loading('Salvando Anexos');

    try {
      const formData = new FormData();
      formData.append('file', fileToSend);

      await api.post(`/material-acessorio/${referenciaId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error: any) {
      const mensagem = `Não foi possível prosseguir com o upload do arquivo. ${error?.response?.data?.message}`;

      throw mensagem;
    } finally {
      setFileToSend('');

      const fileInput = document.getElementById(
        'fileUpload',
      ) as HTMLInputElement;

      if (fileInput) {
        fileInput.value = '';
      }

      toast.dismiss(toastIdAnexo);
    }
  }

  async function handleAprovacao(aprovar: boolean) {
    if (!dados) return;

    // Salvar registro
    setIsLoading(true);
    const toastIdCriacao = toast.loading('Salvando informações');
    await api.post(`/material-acessorio/${dados.id}/aprovacao`, {
      aprovado: aprovar,
      quantidade,
    });

    toast.dismiss(toastIdCriacao);
    const toastIdAnexo = toast.loading('Salvando Anexos');

    if (fileToSend) await uploadFile(dados.id);

    toast.dismiss(toastIdAnexo);

    const toastIdAlerta = toast.loading('Enviando Alerta');

    await api.post(
      `/material-acessorio/${dados.id}/disparar-mensagem-aprovacao`,
      {},
    );

    toast.dismiss(toastIdAlerta);

    toast.success('Processo concluído');

    setIsOpen(false);
    setIsLoading(false);
    changeStatus(dados.id, 'S');
  }

  async function handleAprovar() {
    const resultado = await Swal.fire({
      title: `Deseja prosseguir com a aprovação? O material não poderá ser modificado e ficará com a quantidade ${quantidade}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Sim, prosseguir`,
      confirmButtonColor: '#d33',
      cancelButtonText: `Fechar`,
    });

    if (resultado.isConfirmed) {
      handleAprovacao(true);
    }
  }

  async function handleReprovar() {
    const resultado = await Swal.fire({
      title: `Deseja prosseguir com a reprovação? O usuário será notificado e o material precisará passar por aprovação novamente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Sim, prosseguir`,
      confirmButtonColor: '#d33',
      cancelButtonText: `Fechar`,
    });

    if (resultado.isConfirmed) {
      handleAprovacao(false);
    }
  }

  const readUploadFile = async (e: any) => {
    e.preventDefault();
    if (e.target.files) {
      setFileToSend(e.target.files[0]);
    }
  };

  return (
    <Modal isOpen={isOpen} size="lg" className="modal-dialog-centered">
      <ModalHeader toggle={() => setIsOpen(false)}>
        Solicitação de Aprovação [{enumStatus[String(dados?.status ?? 'P')]}]:
        <div>
          <small>
            {dados?.material.codigoSap} - {dados?.material.descricao}
          </small>
        </div>
        <div className="mt-1">
          <small>
            Configuração #{dados?.orcamentoId} | GNF:{' '}
            {dados?.orcamento?.codigoOportunidadeDomsge}
          </small>
        </div>
        <div>
          <small>
            Cliente: {dados?.orcamento?.pessoa?.codigoDomsge} -{' '}
            {dados?.orcamento?.pessoa?.razaoSocial}
          </small>
        </div>
        <div className="mt-1">
          <small>
            {`Solicitado por: ${String(
              dados?.usuarioSolicitante.username,
            ).toUpperCase()}`}
          </small>
        </div>
        <div>
          <small>{`Perfil Responsável: ${dados?.perfil.nome} | Status: ${
            enumStatus[String(dados?.status ?? 'P')]
          }`}</small>
        </div>
        <div>
          {dados?.usuarioAprovacao
            ? `${enumStatus[String(dados?.status ?? 'P')]} por: ${String(
                dados.usuarioAprovacao.username,
              ).toUpperCase()}`
            : ``}
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        <Row className="mt-1">
          <Col sm="12">
            <TextField
              variant="outlined"
              label="Descrição da Solicitação"
              multiline
              rows={5}
              disabled
              value={descricao ?? ''}
              onChange={event => setDescricao(event.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                style: {
                  minHeight: 120,
                  alignItems: 'flex-start',
                },
              }}
            />
          </Col>
        </Row>

        <Row className="mt-2">
          <Col sm="12">
            <TextField
              variant="outlined"
              label="Quantidade Aplicar"
              value={quantidade}
              onChange={event => setQuantidade(Number(event.target.value))}
            />
          </Col>
        </Row>

        <Row className="mt-1">
          <Col sm="12">
            <strong>Anexos:</strong>
            <br />
            <input
              hidden={isBlocked}
              type="file"
              name="fileUpload"
              id="fileUpload"
              onChange={readUploadFile}
            />
            <Table>
              {dados?.MaterialAcessorioAnexos?.map(it => {
                return (
                  <>
                    <tr>
                      <td>{it.arquivo.nome} </td>
                      <td>
                        <Button
                          color="primary"
                          type="button"
                          size="sm"
                          className="float-right"
                          outline
                          disabled={isLoading}
                          title="Baixar arquivo"
                          onClick={() =>
                            downloadFileFromUrl(it.arquivo.url, it.arquivo.nome)
                          }
                        >
                          <FaDownload />
                        </Button>
                      </td>
                    </tr>
                  </>
                );
              })}
            </Table>
          </Col>
        </Row>

        <Row className="mt-3" hidden={dados?.status === 'S'}>
          <Col sm="6">
            <Button
              className="w-100"
              color="danger"
              type="button"
              disabled={isLoading || isBlocked}
              onClick={handleReprovar}
            >
              Reprovar
            </Button>
          </Col>

          <Col sm="6">
            <Button
              className="w-100"
              color="primary"
              type="button"
              disabled={isLoading || isBlocked}
              onClick={handleAprovar}
            >
              Aprovar
            </Button>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default memo(ModalMaterialAcessorioAprovacao);
